import {
  CHAR_MAX,
  deinterleave,
  EoReader,
  EoWriter,
  encodeNumber,
  flipMsb,
  interleave,
  type Packet,
  PacketAction,
  PacketFamily,
  PacketSequencer,
  SequenceStart,
  swapMultiples,
} from 'eolib';
export class PacketBus {
  private socket?: WebSocket;
  private cancelConnect?: () => void;
  private sequencer?: PacketSequencer;
  private encodeMultiple = 0;
  private decodeMultiple = 0;
  private handlers: Map<
    PacketFamily,
    Map<PacketAction, (reader: EoReader) => void>
  > = new Map();

  onPacketSent?: (
    family: PacketFamily,
    action: PacketAction,
    rawBytes: Uint8Array,
    payload: Uint8Array,
  ) => void;

  onPacketReceived?: (
    family: PacketFamily,
    action: PacketAction,
    rawBytes: Uint8Array,
    payload: Uint8Array,
  ) => void;

  async connect(url: string, onClose: () => void): Promise<void> {
    // Retire old listeners before replacing transport/sequence state.
    this.disconnect();
    this.encodeMultiple = 0;
    this.decodeMultiple = 0;
    return new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(url);
      this.socket = socket;
      socket.binaryType = 'arraybuffer';
      this.sequencer = new PacketSequencer(SequenceStart.zero());
      let opened = false;
      let finished = false;
      let messages = Promise.resolve();
      const current = () => this.socket === socket;
      const fail = (error: Error) => {
        if (finished) return;
        finished = true; clearTimeout(timeout);
        if (current()) { this.socket = undefined; this.sequencer = undefined; this.cancelConnect = undefined; }
        socket.close(); reject(error);
      };
      const timeout = setTimeout(() => {
        if (!opened && current()) fail(new Error('Connection timed out'));
      }, 15000);
      this.cancelConnect = () => fail(new Error('Connection cancelled'));
      socket.addEventListener('open', () => {
        if (!current() || finished) return;
        opened = true; finished = true; clearTimeout(timeout);
        this.cancelConnect = undefined; resolve();
      });
      socket.addEventListener('close', () => {
        if (!current()) return;
        if (!opened) { fail(new Error('Connection closed before opening')); return; }
        clearTimeout(timeout); this.socket = undefined; this.sequencer = undefined;
        onClose();
      });
      socket.addEventListener('error', () => {
        if (!current()) return;
        if (!opened) fail(new Error('Unable to connect'));
        else socket.close();
      });
      socket.addEventListener('message', e => {
        if (!current()) return;
        // Preserve receive order even if a browser supplies asynchronous Blobs.
        messages = messages.then(async () => {
          const buf = e.data instanceof ArrayBuffer ? e.data : await e.data.arrayBuffer();
          if (current()) this.handlePacket(new Uint8Array(buf));
        }).catch(error => console.error('Failed to process game packet', error));
      });
    });
  }

  disconnect() {
    const socket = this.socket;
    const cancel = this.cancelConnect;
    this.cancelConnect = undefined;
    this.socket = undefined;
    this.sequencer = undefined;
    cancel?.();
    socket?.close();
  }

  setSequence(sequence: SequenceStart) {
    this.sequencer!.sequenceStart = sequence;
  }

  setEncryption(encodeMultiple: number, decodeMultiple: number) {
    this.encodeMultiple = encodeMultiple;
    this.decodeMultiple = decodeMultiple;
  }

  private handlePacket(buf: Uint8Array) {
    const data = buf.slice(2);
    if (data[0] !== 0xff && data[1] !== 0xff) {
      deinterleave(data);
      flipMsb(data);
      swapMultiples(data, this.decodeMultiple);
    }

    const action = data[0];
    const family = data[1];

    const packetBuf = data.slice(2);

    this.onPacketReceived?.(family, action, new Uint8Array(data), packetBuf);

    const reader = new EoReader(packetBuf);
    const familyHandlers = this.handlers.get(family);
    if (familyHandlers) {
      const handler = familyHandlers.get(action);
      if (handler) {
        handler(reader);
      } else {
        console.error(
          `Unhandled packet: ${PacketFamily[family]}_${PacketAction[action]}`,
        );
      }
    } else {
      console.error(
        `Unhandled packet: ${PacketFamily[family]}_${PacketAction[action]}`,
      );
    }
  }

  send(packet: Packet) {
    const writer = new EoWriter();
    packet.serialize(writer);
    this.sendBuf(packet.family, packet.action, writer.toByteArray());
  }

  sendBuf(family: PacketFamily, action: PacketAction, buf: Uint8Array) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.sequencer) return;
    const data = [...buf];
    const sequence = this.sequencer!.nextSequence();

    if (action !== 0xff && family !== 0xff) {
      const sequenceBytes = encodeNumber(sequence);
      if (sequence > CHAR_MAX) {
        data.unshift(sequenceBytes[1]);
      }
      data.unshift(sequenceBytes[0]);
    }

    data.unshift(family);
    data.unshift(action);

    // Log pre-encryption: rawBytes includes action + family + seq + payload.
    // payload (buf) is the clean serialized packet data without seq bytes.
    this.onPacketSent?.(family, action, new Uint8Array(data), buf);

    const temp = new Uint8Array(data);

    if (data[0] !== 0xff && data[1] !== 0xff) {
      swapMultiples(temp, this.encodeMultiple);
      flipMsb(temp);
      interleave(temp);
    }

    const lengthBytes = encodeNumber(temp.length);

    const payload = new Uint8Array([lengthBytes[0], lengthBytes[1], ...temp]);
    this.socket?.send(payload);
  }

  registerPacketHandler(
    family: PacketFamily,
    action: PacketAction,
    callback: (reader: EoReader) => void,
  ) {
    if (!this.handlers.has(family)) {
      this.handlers.set(family, new Map());
    }

    const actionMap = this.handlers.get(family);
    actionMap!.set(action, callback);
  }
}
